import {
    ComponentType,
    Dispatch,
    RefObject,
    ReactNode,
    SetStateAction,
} from 'react';
import {
    GridColumnVisibilityModel,
    GridPinnedColumnFields,
    GridDensity,
    GridFilterModel,
    GridColDef,
    GridColumnMenuProps,
    GridColumnsPanelProps,
    GridRowGroupingModel,
    GridSortItem,
    GridToolbarProps,
    ToolbarPropsOverrides,
} from '@mui/x-data-grid-premium';
import moment from 'moment';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { AdvancedFiltersState } from 'common/context/DataGridAdvancedFilterContext';
import { ContractorServiceMapView } from './contractors';
import { DistributionCentersAndInventoryView } from './distributionCenterAndInventory';

declare module '@mui/x-data-grid-premium' {
    interface ColumnsPanelPropsOverrides extends GridColumnsPanelProps {
        gridName: GRID_NAME;
        apiRef?: RefObject<GridApiPremium>;
    }
    interface ColumnMenuPropsOverrides {
        gridName: GRID_NAME;
        colDef?: GridColDef;
    }

    interface FooterPropsOverrides {
        currentRows: number;
        totalRows: number;
        onPageClick: (page: number) => void;
        activeRecordsCount: number;
        gridName: GRID_NAME;
    }

    interface ToolbarPropsOverrides {
        apiRef: RefObject<GridApiPremium>;
        drawerOpen: boolean;
        isFullScreen: boolean;
        gridName: GRID_NAME;
        toggleFullscreen: () => void;
        resetColumnWidth: () => void;
        openAdvancedFilters: () => void;
        saveGrid: () => void;
        loadGridView: (gridView: DataGridStateView<DataGridStateType>) => void;
        resetGrid: () => void;
        deleteGrid: (gridView: DataGridStateView<DataGridStateType>) => void;
        editGrid: (gridView: DataGridStateView<DataGridStateType>) => void;
        handleExport: (
            format: string,
            apiRef: RefObject<GridApiPremium>
        ) => void;
        exportCount: number;
        showSavedGridViews: boolean;
        showSharedViews: boolean;
        showGridActionButton: boolean;
        showGridActionButtonBefore: boolean;
        showAdvancedFilters: boolean;
        showResetColumnWidthsButton: boolean;
        showExportButton: boolean;
        showExpand: boolean;
        openShareViewDrawer: boolean;
        setOpenShareViewDrawer: Dispatch<SetStateAction<boolean>>;
        hasDefaultView: boolean;
        gridActionButtons?: ReactNode;
        gridActionButtonsBefore?: ReactNode;
        showQuickActions?: boolean;
        exportCountLimit?: number;
    }

    interface BasePopperPropsOverrides {
        container: HTMLElement;
    }

    interface BaseCheckboxPropsOverrides {
        name: string;
        checked: boolean;
        disabled?: boolean;
    }

    interface NoRowsOverlayPropsOverrides {
        customNoRowsOverlayMessage?: string;
        customNoRowsOverlayComponent?: ReactNode;
    }

    interface PaginationPropsOverrides {
        recordsPerPage: number;
        activePage: number;
        totalRecords: number;
        activeRecordsCount: number;
        onPageClick: (page: number) => void;
    }

    interface LoadingOverlayPropsOverrides {
        showLoadingText?: boolean;
    }
}

export interface CustomToolbar {
    component: ComponentType<GridToolbarProps & ToolbarPropsOverrides>;
    props?: Record<string, any>;
}

export type GridColumnDimensionProperties =
    'maxWidth' | 'minWidth' | 'width' | 'flex';

export type GridColumnDimensions = {
    [key in GridColumnDimensionProperties]?: number;
};

export interface DataGridState<DataType = any> {
    data: DataType[];
    totalCount: number;
    activePaginationPage: number;
    dataLoading: boolean;
    initialized: boolean;
    activeQuery: {
        where?: object;
        limit?: number;
        skip?: number;
        order?: string[];
    };
    columnBufferPx: number;
    columnOrdering: string[];
    columnVisibility: GridColumnVisibilityModel;
    pinnedColumns: GridPinnedColumnFields;
    filterModel: GridFilterModel;
    columnDimensions: Record<string, GridColumnDimensions>;
    density: GridDensity;
    activeFilters: DataGridFilter[];
    searchFilters: Record<string, DataGridSearchFilter[]>;
    advancedFilters: Partial<AdvancedFiltersState>;
    advancedFilterCount: number;
    activeId: number;
    sortModel: GridSortItem[];
    rowGroupingModel: GridRowGroupingModel;
    disableQueryRules: boolean;
    clearAllAdvancedFilterId: string;
}

export interface DataGridStateView<T> {
    id: number;
    accountId: number;
    title: string;
    gridName: GRID_NAME;
    gridState: T;
    sharedViewUserIds: number[];
    isDefaultView: boolean;
}

export type AdvancedFilter = {
    id: number;
    display: string;
    target: string;
    term: string | Date | boolean;
};

export interface SearchCriteria {
    field: {
        property: string;
        id?: string;
        display?: string;
        searchQuery?: (filter: DataGridFilter) => Promise<any>;
        valueFormatter?: (value: string) => string;
    };
    operator: {
        id: string;
        display?: string;
    };
    searchTerm: {
        value: string | string[] | {};
        display?: string;
    };
    target?: string;
}

export interface SearchCriteriaV2 {
    field: {
        id: string;
        display: string;
        searchQuery?: (searchTerm: string | {}) => Promise<any>;
        valueFormatter?: (value: string) => string;
        minChars?: number;
    };
    searchTerm: {
        value: string | string[] | {};
        display: string;
    };
}

export interface DataGridSearchFilter {
    searchTerm: {
        value: string | string[] | {};
    };
    display: string;
    term?: string | string[] | {};
}

export interface DataGridAdvancedFilter {
    display: string;
    id?: string;
    term?: string | number | boolean | moment.Moment | string[];
    form?: {
        id?: string | number | boolean;
        display?: string;
        segmentId?: string;
        reasons?: {};
        parentId?: string | number;
        value?: string | boolean | number;
        label?: string;
    };
    isDefault?: boolean;
    distance?: {
        id: number;
        display: string;
    };
    issueReasonIds?: {
        term: string;
        form: { id: string; parentId: string };
    }[];
}

// combines the grid filter item (GridFilterItem) model with our advanced filter model (AdvancedFilter)
export interface DataGridFilter {
    id?: string | number;
    display?: string;
    target?: string;
    term?: string | [] | {} | boolean;
    field?: {
        property: string;
        display?: string;
    };
    operator?: {
        id: string;
        display?: string;
    };
    searchTerm?: {
        value: string | string[] | {};
        display?: string;
    };
    searchQuery?: (filter: DataGridFilter) => Promise<any>;
    form?: {
        id?: string | number | boolean;
        display?: string;
        segmentId?: string;
        reasons?: {};
        parentId?: number;
        value?: string | boolean | number;
        label?: string;
    };
}
export interface LoopbackFilter {
    where: Record<string, any>;
    skip?: number;
    order?: string[];
    limit?: number;
}

export interface TablePagination {
    activePage: number;
    countOfResults: number;
    activeRecordsCount?: number;
    resultsPerPage: number;
    handlePagination?: (page: number) => void;
}

export interface DataGridColumnMenuProps extends GridColumnMenuProps {
    gridName: GRID_NAME;
    colDef: GridColDef;
}

export enum GRID_NAME {
    ADMIN_ASSET = 'ADMIN_ASSET',
    ADMIN_ORDER = 'ADMIN_ORDER',
    ADMIN_ORDER_DRAWER = 'ADMIN_ORDER_DRAWER',
    ADMIN_ORDER_RULE = 'ADMIN_ORDER_RULE',
    ADMIN_WORK_ORDER = 'ADMIN_WORK_ORDER',
    ADMIN_CONTRACTOR = 'ADMIN_CONTRACTOR',
    ADMIN_PROJECT = 'ADMIN_PROJECT',
    ADMIN_PROJECT_LIST = 'ADMIN_PROJECT_LIST',
    ADMIN_PROJECT_WORK_ORDER_LIST = 'ADMIN_PROJECT_WORK_ORDER_LIST',
    ADMIN_TEMPLATE_QUOTE = 'ADMIN_TEMPLATE_QUOTE',
    ADMIN_TEMPLATE_CLIENT = 'ADMIN_TEMPLATE_CLIENT',
    ADMIN_TEMPLATE_CHECKLIST = 'ADMIN_TEMPLATE_CHECKLIST',
    ADMIN_QUOTE = 'ADMIN_QUOTE',
    ADMIN_QUOTE_EQUIPMENT_ONLY = 'ADMIN_QUOTE_EQUIPMENT_ONLY',
    ADMIN_FINANCE_SYNC_TASK = 'ADMIN_FINANCE_SYNC_TASK',
    ADMIN_FINANCE_SALES_INVOICE = 'ADMIN_FINANCE_SALES_INVOICE',
    ADMIN_FINANCE_PURCHASE_INVOICE = 'ADMIN_FINANCE_PURCHASE_INVOICE',
    ADMIN_FINANCE_POSTAL_CODE_LABOR_RATE = 'ADMIN_FINANCE_POSTAL_CODE_LABOR_RATE',
    ADMIN_FINANCE_CONTRACTOR_LABOR_RATE = 'ADMIN_FINANCE_CONTRACTOR_LABOR_RATE',
    ADMIN_FINANCE_CLIENT_LABOR_RATE = 'ADMIN_FINANCE_CLIENT_LABOR_RATE',
    ADMIN_FINANCE_PROJECT_LABOR_RATE = 'ADMIN_FINANCE_PROJECT_LABOR_RATE',
    ADMIN_CONTRACTOR_FILE = 'ADMIN_CONTRACTOR_FILE',
    ADMIN_CONTRACTOR_REGISTRATION = 'ADMIN_CONTRACTOR_REGISTRATION',
    ADMIN_TSM_MANAGEMENT = 'ADMIN_TSM_MANAGEMENT',
    ADMIN_INVOICE = 'ADMIN_INVOICE',
    ADMIN_ORDER_CLONE = 'ADMIN_ORDER_CLONE',
    AHRI = 'AHRI',
    CLIENT_ORDER = 'CLIENT_ORDER',
    CLIENT_PROPERTY = 'CLIENT_PROPERTY',
    CLIENT_INVOICE = 'CLIENT_INVOICE',
    CLIENT_SHARED_INVOICE = 'CLIENT_SHARED_INVOICE',
    CLIENT_TEAMS = 'CLIENT_TEAMS',
    CLIENT_MANAGE_CHILD_CLIENTS = 'CLIENT_MANAGE_CHILD_CLIENTS',
    CLIENT_TEAM_PERMISSIONS = 'CLIENT_TEAM_PERMISSIONS',
    DISTRIBUTION_INVENTORY = 'DISTRIBUTION_INVENTORY',
    PRODUCT_SELECTOR = 'PRODUCT_SELECTOR',
    PRODUCT_SEARCH_LIST = 'PRODUCT_SEARCH_LIST',
    SAVED_ORDER = 'SAVED_ORDER',
    SHARED_SAVED_ORDER = 'SHARED_SAVED_ORDER',
    WARRANTY_CONTACT = 'WARRANTY_CONTACT',
    WARRANTY_OWNER = 'WARRANTY_OWNER',
    DISTRIBUTION_CENTER_AND_INVENTORY_SEARCH = 'DISTRIBUTION_CENTER_AND_INVENTORY_SEARCH',
    DISTRIBUTION_CENTER_AND_INVENTORY_SEARCH_CONTRACTOR = 'DISTRIBUTION_CENTER_AND_INVENTORY_SEARCH_CONTRACTOR',
    DISTRIBUTION_CENTER_AND_INVENTORY_SEARCH_CLIENT = 'DISTRIBUTION_CENTER_AND_INVENTORY_SEARCH_CLIENT',
    TAX_TRANSACTION_HISTORY = 'TAX_TRANSACTION_HISTORY',
    INVOICE_DOWNLOAD_HISTORIES = 'INVOICE_DOWNLOAD_HISTORIES',
    CONTRACTOR_ORDERS = 'CONTRACTOR_ORDERS',
    CONTRACTOR_INVOICE = 'CONTRACTOR_INVOICE',
    CONTRACTOR_SHARED_INVOICE = 'CONTRACTOR_SHARED_INVOICE',
    CONTRACTOR_QUOTES = 'CONTRACTOR_QUOTES',
    CONTRACTOR_WORK_ORDERS = 'CONTRACTOR_WORK_ORDERS',
    CONTRACTOR_AVAILABLE_JOBS = 'CONTRACTOR_AVAILABLE_JOBS',
    CONTRACTOR_TEAMS = 'CONTRACTOR_TEAMS',
    CONTRACTOR_TEAM_FILES = 'CONTRACTOR_TEAM_FILES',
    CONTRACTOR_FILES = 'CONTRACTOR_FILES',
    DOCUMENT_FILES = 'DOCUMENT_FILES',
    LINE_ITEM_ORDER = 'LINE_ITEM_ORDER',
    LINE_ITEM_WORK_ORDER = 'LINE_ITEM_WORK_ORDER',
    LINE_ITEM_QUOTE = 'LINE_ITEM_QUOTE',
    LINE_ITEM_ORDER_LABOR_WARRANTY_CREATION = 'LINE_ITEM_ORDER_LABOR_WARRANTY_CREATION',
    LINE_ITEM_ORDER_LABOR_WARRANTY = 'LINE_ITEM_ORDER_LABOR_WARRANTY',
    LINE_ITEM_ORDER_LABOR_WARRANTY_FULFILL_MODAL = 'LINE_ITEM_ORDER_LABOR_WARRANTY_FULFILL_MODAL',
    LINE_ITEM_ORDER_FULFILL_MODAL = 'LINE_ITEM_ORDER_FULFILL_MODAL',
    LINE_ITEM_RE_ORDER_MODAL = 'LINE_ITEM_RE_ORDER_MODAL',
    LINE_ITEM_WORK_ORDER_COMPLETE_MODAL = 'LINE_ITEM_WORK_ORDER_COMPLETE_MODAL',
    LINE_ITEM_ORDER_CONFIRMATION_MODAL_WITH_PRICE = 'LINE_ITEM_ORDER_CONFIRMATION_MODAL_WITH_PRICE',
    LINE_ITEM_ORDER_CONFIRMATION_MODAL_WITHOUT_PRICE = 'LINE_ITEM_ORDER_CONFIRMATION_MODAL_WITHOUT_PRICE',
    LINE_ITEM_QUOTE_APPROVE_MODAL = 'LINE_ITEM_QUOTE_APPROVE_MODAL',
    LINE_ITEM_QUOTE_CONFIRM_ORDER_MODAL = 'LINE_ITEM_QUOTE_CONFIRM_ORDER_MODAL',
    LINE_ITEM_INSPECTION_WORK_ORDER_SUBMIT_MODAL = 'LINE_ITEM_INSPECTION_WORK_ORDER_SUBMIT_MODAL',
    LINE_ITEM_WORK_ORDER_SUBMIT_MODAL_WITH_STATUS = 'LINE_ITEM_WORK_ORDER_SUBMIT_MODAL_WITH_STATUS',
    LINE_ITEM_WORK_ORDER_SUBMIT_MODAL_WITHOUT_STATUS = 'LINE_ITEM_WORK_ORDER_SUBMIT_MODAL_WITHOUT_STATUS',
    LINE_ITEM_ORDER_QUOTE_GENERATE_PDF_MODAL = 'LINE_ITEM_ORDER_QUOTE_GENERATE_PDF_MODAL',
    LINE_ITEM_QUOTE_GENERATE_PDF_PRICE = 'LINE_ITEM_QUOTE_GENERATE_PDF_PRICE',
    LINE_ITEM_QUOTE_GENERATE_PDF_PAYOUT = 'LINE_ITEM_QUOTE_GENERATE_PDF_PAYOUT',
    LINE_ITEM_ORDER_GENERATE_PDF_MODAL = 'LINE_ITEM_ORDER_GENERATE_PDF_MODAL',
    LINE_ITEM_ORDER_CONTRACTOR_INVOICED = 'LINE_ITEM_ORDER_CONTRACTOR_INVOICED',
    LINE_ITEM_DRAFT = 'LINE_ITEM_DRAFT',
    LINE_ITEM_BULK_QUOTE = 'LINE_ITEM_BULK_QUOTE',
    LINE_ITEM_BULK_WORK_ORDER = 'LINE_ITEM_BULK_WORK_ORDER',
    LINE_ITEM_QUOTE_TEMPLATE = 'LINE_ITEM_QUOTE_TEMPLATE',
    LINE_ITEM_PURCHASE_INVOICE = 'LINE_ITEM_PURCHASE_INVOICE',
    LINE_ITEM_SALES_INVOICE = 'LINE_ITEM_SALES_INVOICE',
    LINE_ITEM_PURCHASE_INVOICE_ADJUSTMENT = 'LINE_ITEM_PURCHASE_INVOICE_ADJUSTMENT',
    LINE_ITEM_SALES_INVOICE_ADJUSTMENT = 'LINE_ITEM_SALES_INVOICE_ADJUSTMENT',
    LINE_ITEM_RETURN = 'LINE_ITEM_RETURN',
    LINE_ITEM_PAST_RETURN = 'LINE_ITEM_PAST_RETURN',
    LINE_ITEM_CREATE_RETURN = 'LINE_ITEM_CREATE_RETURN',
    LINE_ITEM_WORK_ORDER_GENERATE_PDF = 'LINE_ITEM_WORK_ORDER_GENERATE_PDF',
    LINE_ITEM_PROPERTY_HISTORY = 'LINE_ITEM_PROPERTY_HISTORY',
    LINE_ITEM_PROPERTY_HISTORY_WITH_STATUS = 'LINE_ITEM_PROPERTY_HISTORY_WITH_STATUS',
    LINE_ITEM_FINANCE = 'LINE_ITEM_FINANCE',
    LINE_ITEM_FINANCE_RETURN = 'LINE_ITEM_FINANCE_RETURN',
    LINE_ITEM_FINANCE_CREATE_CREDIT = 'LINE_ITEM_FINANCE_CREATE_CREDIT',
    SERIAL_NUMBER_SEARCH = 'SERIAL_NUMBER_SEARCH',
}

export enum SAVE_TYPE {
    CREATE_NEW = 'CREATE_NEW',
    UPDATE_CURRENT = 'UPDATE_CURRENT',
}

export interface ColumnDelta {
    remove?: string[];
    add?: { field: string; after: string }[];
}

export type DataGridStore = Record<GRID_NAME, DataGridState>;

export type DataGridPayLoad<DataType = any> = DataGridState<DataType> & {
    initialState: Partial<DataGridState<DataType>>;
    gridName: GRID_NAME;
    reset?: boolean;
    concatenateResults?: boolean;
};

export type DataGridStateType =
    | DataGridState
    | DistributionCentersAndInventoryView
    | ContractorServiceMapView;

export const COLUMN_MIN_WIDTH = {
    SMALL: 120,
    MEDIUM: 160,
    LARGE: 220,
} as const;
